/**
 * Global mixin for Form.vue components to support pre-filling from chatbot.
 * Reads `?prefill=base64json` query param and applies to `this.form`.
 *
 * Registered globally in main.js — no per-form imports needed.
 * Waits for the form's async mounted() to complete before applying.
 * For ID fields (partyLedgerId, godownId, etc.), fetches the entity
 * to populate the LiveSearchSelect cache.
 */
import api from "@/helpers/api";

// Maps form field keys to API endpoints + all known options array names across forms.
// altOptionsKeys covers forms that use non-standard names for the same entity.
const FIELD_FETCH_MAP = {
  // Ledger-based fields (parties, brokers, transporters, etc.)
  partyLedgerId: {
    endpoint: "/ledgers",
    optionsKey: "partyOptions",
    altOptionsKeys: ["vendorOptions", "customerOptions", "ledgerOptions"],
  },
  billingPartyLedgerId: {
    endpoint: "/ledgers",
    optionsKey: "billingPartyOptions",
    altOptionsKeys: ["partyOptions"],
  },
  consigneeLedgerId: {
    endpoint: "/ledgers",
    optionsKey: "consigneeOptions",
  },
  brokerLedgerId: {
    endpoint: "/ledgers",
    optionsKey: "brokerOptions",
  },
  transporterId: {
    endpoint: "/ledgers",
    optionsKey: "transporterOptions",
  },
  purchaseLedgerId: {
    endpoint: "/ledgers",
    optionsKey: "expenseLedgerOptions",
    altOptionsKeys: ["ledgerOptions"],
  },
  salesLedgerId: {
    endpoint: "/ledgers",
    optionsKey: "salesLedgerOptions",
    altOptionsKeys: ["ledgerOptions"],
  },

  // Godown fields
  godownId: {
    endpoint: "/godowns",
    optionsKey: "godownOptions",
    altOptionsKeys: ["leafGodownOptions"],
  },
  parentGodownId: {
    endpoint: "/godowns",
    optionsKey: "godownOptions",
    altOptionsKeys: ["parentGodownOptions"],
  },
  productionGodownId: {
    endpoint: "/godowns",
    optionsKey: "godownOptions",
  },

  // Account groups
  accountGroupId: {
    endpoint: "/account-groups",
    optionsKey: "groupOptions",
    altOptionsKeys: ["accountGroupOptions"],
  },
  parentGroupId: {
    endpoint: "/account-groups",
    optionsKey: "groupOptions",
    altOptionsKeys: ["accountGroupOptions", "parentGroupOptions"],
  },

  // Stock / inventory
  stockGroupId: {
    endpoint: "/stock-groups",
    optionsKey: "groupOptions",
    altOptionsKeys: ["stockGroupOptions"],
  },
  categoryId: {
    endpoint: "/categories",
    optionsKey: "categoryOptions",
    altOptionsKeys: ["subCategoryOptions", "parentCategoryOptions"],
  },
  subCategoryId: {
    endpoint: "/categories",
    optionsKey: "subCategoryOptions",
    altOptionsKeys: ["categoryOptions"],
  },
  uomId: {
    endpoint: "/units-of-measurement",
    optionsKey: "uomOptions",
  },
  alternateUOMId: {
    endpoint: "/units-of-measurement",
    optionsKey: "uomOptions",
  },
  hsnSacId: {
    endpoint: "/hsn-sac",
    optionsKey: "hsnOptions",
    altOptionsKeys: ["hsnSacOptions"],
  },

  // Tax
  taxRateId: {
    endpoint: "/tax-rates",
    optionsKey: "taxRateOptions",
  },
  freightTaxRateId: {
    endpoint: "/tax-rates",
    optionsKey: "taxRateOptions",
  },

  // Financial year
  financialYearId: {
    endpoint: "/financial-years",
    optionsKey: "financialYearOptions",
    altOptionsKeys: ["fyOptions"],
  },

  // Stock item lookups (make, mill, grade)
  makeId: {
    endpoint: "/stock-item-lookups",
    optionsKey: "makeOptions",
  },
  millId: {
    endpoint: "/stock-item-lookups",
    optionsKey: "millOptions",
  },
  gradeLookupId: {
    endpoint: "/stock-item-lookups",
    optionsKey: "gradeOptions",
  },

  // Company (shared forms)
  companyId: {
    endpoint: "/companies",
    optionsKey: "companyOptions",
  },
};

export default {
  data() {
    return {
      prefillApplied: false,
    };
  },
  mounted() {
    // Early exit — avoid any work on components without prefill query param
    const prefill = this.$route?.query?.prefill;
    if (!prefill) return;
    // Only apply to form components (have a `form` data property)
    if (!this.form) return;
    // Prevent double-apply
    if (this.prefillApplied) return;

    this._applyPrefill(prefill);
  },
  methods: {
    async _applyPrefill(prefill) {
      if (this.prefillApplied) return;

      // Wait for async mounted() to finish (loading flag goes false)
      let attempts = 0;
      while (this.loading && attempts < 50) {
        await new Promise((r) => setTimeout(r, 100));
        attempts++;
      }

      // Extra tick for DOM readiness
      await this.$nextTick();
      await new Promise((r) => setTimeout(r, 300));

      try {
        const data = JSON.parse(atob(prefill));
        if (typeof data !== "object" || data === null) return;

        // Apply values to form
        for (const [key, value] of Object.entries(data)) {
          if (!(key in this.form) || value === null || value === undefined)
            continue;

          // Convert string numbers to actual numbers for ID fields
          if (
            key.endsWith("Id") &&
            typeof value === "string" &&
            /^\d+$/.test(value)
          ) {
            this.form[key] = Number(value);
          } else {
            this.form[key] = value;
          }
        }

        // For ID fields, fetch the entity to populate LiveSearchSelect options
        for (const [key, value] of Object.entries(data)) {
          if (!key.endsWith("Id") || !value) continue;
          const numericId =
            typeof value === "number"
              ? value
              : typeof value === "string" && /^\d+$/.test(value)
                ? Number(value)
                : null;
          if (!numericId) continue;

          const mapping = FIELD_FETCH_MAP[key];
          if (!mapping) continue;

          try {
            const { data: entity } = await api.get(
              `${mapping.endpoint}/${numericId}`
            );
            if (entity) {
              const option = {
                id: entity.id,
                text: entity.name || entity.fullName || entity.text || `#${entity.id}`,
              };

              // Push to options array — check primary key and alternate keys
              const keysToTry = [
                mapping.optionsKey,
                ...(mapping.altOptionsKeys || []),
              ];
              for (const optKey of keysToTry) {
                if (this[optKey] && Array.isArray(this[optKey])) {
                  if (!this[optKey].find((o) => o.id === option.id)) {
                    this[optKey].push(option);
                  }
                  break;
                }
              }
            }
          } catch {
            // Entity fetch failed — field ID is still set, just no label
          }
        }

        // Handle line items (from chatbot form-fill with stock item extraction)
        if (data._lineItems && Array.isArray(data._lineItems) && this.lineItems) {
          for (const lineData of data._lineItems) {
            const stockItemId = lineData.stockItemId
              ? Number(lineData.stockItemId)
              : null;

            if (stockItemId) {
              // Ensure stock item is cached for LiveSearchSelect label resolution
              if (
                this.stockItemOptions &&
                !this.stockItemOptions.find((o) => o.id === stockItemId)
              ) {
                try {
                  const { data: si } = await api.get(
                    `/stock-items/${stockItemId}`
                  );
                  if (si) {
                    this.stockItemOptions.push({
                      id: si.id,
                      text: si.name,
                      uomSymbol: si.uomSymbol || "",
                      taxRateId: si.taxRateId,
                    });
                  }
                } catch {
                  // Stock item fetch failed — continue without label
                }
              }

              // Find the first empty line item or add to it
              const emptyLine = this.lineItems.find(
                (li) => !li.stockItemId
              );
              if (emptyLine) {
                emptyLine.stockItemId = stockItemId;
                emptyLine.itemName = lineData.itemName || "";
                if (lineData.quantity)
                  emptyLine.quantity = lineData.quantity;

                // Get UOM symbol from cached options
                const cached = this.stockItemOptions?.find(
                  (o) => o.id === stockItemId
                );
                if (cached?.uomSymbol)
                  emptyLine.uomSymbol = cached.uomSymbol;
              }
            }
          }
        }

        this.prefillApplied = true;

        // Clean up URL
        const query = { ...this.$route.query };
        delete query.prefill;
        this.$router.replace({ query }).catch(() => {});
      } catch {
        // Invalid base64 or JSON — silently ignore
      }
    },
  },
};
